// @ts-check
import { WIZARD_NUMBER_STATES } from 'components/WizardSteps/WizardNumber';

import {
  FirstStep,
  ForthStep,
  InitialStep,
  NextPreviousButtons,
  SecondStep,
  ThirdStep
} from './components';
import {
  CodeText,
  DefaultText,
  StyledPrerequisites
} from './GitstreamWizardModal.style';
import { PROVIDER_NAMES } from 'common/constants/constants';
import { LBButton } from '@linearb/buzz';

const WIDGET_LINE_HEIGHT = '3.5rem';

const STEPS = {
  INITIAL: 'INITIAL',
  FIRST: 'FIRST',
  SECOND: 'SECOND',
  THIRD: 'THIRD',
  FORTH: 'FORTH'
};

export const STEPS_INDEXES = {
  0: STEPS.INITIAL,
  1: STEPS.FIRST,
  2: STEPS.SECOND,
  3: STEPS.THIRD,
  4: STEPS.FORTH
};

const INITIAL_LINE = {
  color: 'dodgerBlue',
  lineStyle: 'solid',
  height: WIDGET_LINE_HEIGHT
};

const STEPS_LINE = {
  color: 'catskillWhite',
  lineStyle: 'dashed',
  height: WIDGET_LINE_HEIGHT
};

export const STEPS_WIZARD_STEPS_PROPS = {
  [STEPS.INITIAL]: {
    steps: [
      { number: 1, state: WIZARD_NUMBER_STATES.INITIAL },
      { number: 2, state: WIZARD_NUMBER_STATES.INITIAL },
      { number: 3, state: WIZARD_NUMBER_STATES.INITIAL }
    ],
    line: INITIAL_LINE
  },
  [STEPS.FIRST]: {
    steps: [
      { number: 1, state: WIZARD_NUMBER_STATES.ACTIVE },
      { number: 2, state: WIZARD_NUMBER_STATES.INACTIVE },
      { number: 3, state: WIZARD_NUMBER_STATES.INACTIVE }
    ],
    line: STEPS_LINE
  },
  [STEPS.SECOND]: {
    steps: [
      { number: 1, state: WIZARD_NUMBER_STATES.DONE },
      { number: 2, state: WIZARD_NUMBER_STATES.ACTIVE },
      { number: 3, state: WIZARD_NUMBER_STATES.INACTIVE }
    ],
    line: STEPS_LINE
  },
  [STEPS.THIRD]: {
    steps: [
      { number: 1, state: WIZARD_NUMBER_STATES.DONE },
      { number: 2, state: WIZARD_NUMBER_STATES.DONE },
      { number: 3, state: WIZARD_NUMBER_STATES.ACTIVE }
    ],
    line: STEPS_LINE
  }
};

const BITBUCKET_PIPELINES = `# Code generated by gitStream - DO NOT EDIT

image: atlassian/default-image:4

pipelines:
  # Pipelines that can only be triggered manually
  custom:
    gitstream:
      - variables:
          - name: client_payload
            description: the client payload
          - name: head_ref
            description: the head sha
          - name: base_ref
            description: The base sha
          - name: resolver_url
            description: the resolver url to pass results to
          - name: resolver_token
            description: Optional resolver token for resolver service
          - name: debug_mode
            description: Debug mode
            default: 'true'
          - name: oauth_token
            description: token to do operations in bitbucket
          - name: full_repo
            description: workspace/repo
      - step:
          name: /:\\ gitstream workflow automation
          max-time: 15
          clone:
            enabled: false
          services:
            - docker
          script:
            - git clone https://x-token-auth:$oauth_token@bitbucket.org/$full_repo.git gitstream/repo
            - git clone https://x-token-auth:$oauth_token@bitbucket.org/$BITBUCKET_WORKSPACE/$BITBUCKET_REPO_SLUG.git gitstream/cm
            - cd gitstream/repo
            - git fetch --all
            - git checkout $base_ref
            - git checkout $head_ref
            - docker pull gitstream/rules-engine:latest
            - |
              docker run -v $BITBUCKET_CLONE_DIR/gitstream:/code \\
              -e HEAD_REF=$head_ref \\
              -e BASE_REF=$base_ref \\
              -e RUN_ID=$BITBUCKET_BUILD_NUMBER \\
              -e CLIENT_PAYLOAD="$client_payload" \\
              -e RULES_RESOLVER_URL=$resolver_url \\
              -e RULES_RESOLVER_TOKEN=$resolver_token \\
              -e DEBUG_MODE=$debug_mode gitstream/rules-engine:latest
`;

const GITLAB_CI = `# Code generated by gitStream - DO NOT EDIT
stages:
  - gitstream-main
image: docker:latest
services:
  - docker:dind
before_script:
  - docker login -u "$CI_REGISTRY_USER" -p "$CI_REGISTRY_PASSWORD" $CI_REGISTRY

gitstream-job:
  stage: gitstream-main
  only:
    variables:
      - $GITSTREAM_MAIN_JOB
  except:
    variables:
      - $GITSTREAM_BLOCK_MERGE
  script:
    - apk update && apk add git && apk add docker
    - git clone https://gitlab-ci-token:$\{CI_JOB_TOKEN}$\{repoUrl} gitstream/repo
    - git clone https://gitlab-ci-token:$\{CI_JOB_TOKEN}$\{cmUrl} gitstream/cm
    - cd gitstream && cd repo && git fetch --all && git checkout $base_ref && git pull && ls && git checkout $head_ref && git pull && ls
    - docker pull gitstream/rules-engine:latest
    - |
      docker run -v $CI_PROJECT_DIR/gitstream:/code \\
      -e HEAD_REF=$head_ref \\
      -e BASE_REF=$base_ref \\
      -e CLIENT_PAYLOAD="$client_payload" \\
      -e RULES_RESOLVER_URL=$resolver_url \\
      -e RULES_RESOLVER_TOKEN=$resolver_token \\
      -e DEBUG_MODE=true  gitstream/rules-engine:latest`;

const GITSTREAM_BITBUCKET_CM =
  `# -*- mode: yaml -*-\n` +
  `# This example configuration for provides basic automations to get started with gitStream.\n` +
  `# View the gitStream quickstart for more examples: https://docs.gitstream.cm/quick-start/\n` +
  `manifest:\n` +
  `  version: 1.0\n` +
  `automations:\n` +
  `  # Add a comment that indicates how many minutes it will take to review the PR.\n` +
  `  estimated_time_to_review:\n` +
  `    if:\n` +
  `      - true\n` +
  `    run:\n` +
  `      - action: add-comment@v1\n` +
  `        args:\n` +
  `          comment: "{{ calc.etr }} min review"\n` +
  `\n` +
  `  # Inform PR authors when they fail to reference Jira tickets in the PR title or description.\n` +
  `  comment_missing_jira_info:\n` +
  `    if:\n` +
  `      - {{ not (has.jira_ticket_in_title or has.jira_ticket_in_desc) }}\n` +
  `    run:\n` +
  `      - action: add-comment@v1\n` +
  `        args:\n` +
  `          comment: |\n` +
  `            This PR is missing a Jira ticket reference in the title or description.\n` +
  `            Please add a Jira ticket reference to the title or description of this PR.\n` +
  `\n` +
  `  # Post a comment that lists the best experts for the files that were modified.\n` +
  `  explain_code_experts:\n` +
  `    if:\n` +
  `      - true\n` +
  `    run:\n` +
  `      - action: explain-code-experts@v1\n` +
  `        args:\n` +
  `          gt: 10\n` +
  `\n` +
  `# +----------------------------------------------------------------------------+\n` +
  `# | Custom Expressions                                                         |\n` +
  `# | https://docs.gitstream.cm/how-it-works/#custom-expressions                 |\n` +
  `# +----------------------------------------------------------------------------+\n` +
  `\n` +
  `calc:\n` +
  `  etr: {{ branch | estimatedReviewTime }}\n` +
  `\n` +
  `has:\n` +
  `  jira_ticket_in_title: {{ pr.title | includes(regex=r/\b[A-Za-z]+-\\d+\\b/) }}\n` +
  `  jira_ticket_in_desc: {{ pr.description | includes(regex=r/atlassian.net\\/browse\\/\\w{1,}-\\d{3,4}/) }}\n`;

const GITSTREAM_GITLAB_CM =
  `# -*- mode: yaml -*-\n` +
  `# This example configuration for provides basic automations to get started with gitStream.\n` +
  `# View the gitStream quickstart for more examples: https://docs.gitstream.cm/quick-start/\n` +
  `manifest:\n` +
  `  version: 1.0\n` +
  `automations:\n` +
  `  # Add a label that indicates how many minutes it will take to review the PR.\n` +
  `  estimated_time_to_review:\n` +
  `    if:\n` +
  `      - true\n` +
  `    run:\n` +
  `      - action: add-label@v1\n` +
  `        args:\n` +
  `          label: "{{ calc.etr }} min review"\n` +
  `          color: {{ 'E94637' if (calc.etr >= 20) else ('FBBD10' if (calc.etr >= 5) else '36A853') }}\n` +
  `  # Post a comment that lists the best experts for the files that were modified.\n` +
  `  explain_code_experts:\n` +
  `    if:\n` +
  `      - true\n` +
  `    run:\n` +
  `      - action: explain-code-experts@v1\n` +
  `        args:\n` +
  `          gt: 10\n` +
  `# The next function calculates the estimated time to review and makes it available in the automation above.\n` +
  `calc:\n` +
  `  etr: {{ branch | estimatedReviewTime }}`;

export const textBySensor = {
  bitbucket: {
    cmFile: GITSTREAM_BITBUCKET_CM,
    title: 'Bitbucket',
    ymlFileName: <CodeText>bitbucket-pipelines.yml</CodeText>,
    prerequisites: (
      <StyledPrerequisites>
        <DefaultText weight='500'>Prerequisites:</DefaultText>
        <DefaultText>Bitbucket cloud</DefaultText>
      </StyledPrerequisites>
    ),
    ymlFileContent: BITBUCKET_PIPELINES,
    designateAccount: (
      <>
        <div style={{ width: '50rem' }}>
          <DefaultText>
            To enable gitStream for BitBucket to support actions like{' '}
            <CodeText>merge</CodeText>
            <DefaultText>,&nbsp;</DefaultText>
            <CodeText>approve</CodeText>
            <DefaultText>, and&nbsp;</DefaultText>
            <CodeText>request-changes</CodeText>
          </DefaultText>
          <DefaultText>
            , you’ll need to re-authenticate and add an additional{' '}
            <CodeText>pull_request:write</CodeText>
            <DefaultText>
              {' '}
              permission beyond those required for integrating BitBucket with
              LinearB.
            </DefaultText>
          </DefaultText>
        </div>
        <DefaultText>
          When using gitStream with Bitbucket, all automations will be triggered
          using the account name that was used for authentication with LinearB.
          Consequently, all automations will be executed under that specific
          account. A meaningful account identifier whose name contains the
          string &nbsp;
        </DefaultText>
        <CodeText>gitStream</CodeText>
        <DefaultText>(case insensitive), such as &nbsp;</DefaultText>
        <CodeText>gitStream-cm</CodeText>
        <DefaultText>
          &nbsp;, is required to ensure clarity and proper identification of the
          automated actions.
        </DefaultText>
      </>
    )
  },
  gitlab: {
    cmFile: GITSTREAM_GITLAB_CM,
    title: 'Gitlab',
    ymlFileName: <CodeText>.gitlab-ci.yml</CodeText>,
    designateAccount: (
      <>
        <DefaultText>
          When using gitStream with Gitlab, all automations will be triggered
          using the account name that was used for authentication with LinearB.
          Consequently, all automations will be executed under that specific
          account. It is recommended to review the account name and consider
          changing it to a more meaningful identifier such as&nbsp;
        </DefaultText>
        <CodeText>gitStream-cm</CodeText>
        <DefaultText>
          &nbsp;to ensure clarity and proper identification of the automated
          actions.
        </DefaultText>
      </>
    ),
    prerequisites: (
      <StyledPrerequisites>
        <DefaultText weight='500'>Prerequisites:</DefaultText>
        <DefaultText>GitLab cloud</DefaultText>
        <DefaultText>GitLab runner v15 or higher</DefaultText>
      </StyledPrerequisites>
    ),
    ymlFileContent: GITLAB_CI
  }
};

export const STEPS_COMPONENTS = {
  [STEPS.INITIAL]: sensorName => <InitialStep sensorName={sensorName} />,
  [STEPS.FIRST]: sensorName => <FirstStep sensorName={sensorName} />,
  [STEPS.SECOND]: sensorName => <SecondStep sensorName={sensorName} />,
  [STEPS.THIRD]: sensorName => <ThirdStep sensorName={sensorName} />,
  [STEPS.FORTH]: (sensorName, sensorAuth) => (
    <ForthStep sensorName={sensorName} sensorAuth={sensorAuth} />
  )
};

export const STEPS_BUTTONS = {
  [STEPS.INITIAL]: ({ onNextClick }) => (
    <LBButton onClick={onNextClick}>Start Installation</LBButton>
  ),
  [STEPS.FIRST]: NextPreviousButtons,
  [STEPS.SECOND]: NextPreviousButtons,
  [STEPS.THIRD]: ({
    onNextClick,
    onPreviousClick,
    onDoneClick,
    sensorName
  }) => (
    <NextPreviousButtons
      onNextClick={
        sensorName === PROVIDER_NAMES.gitlab ? onDoneClick : onNextClick
      }
      onPreviousClick={onPreviousClick}
    />
  ),
  [STEPS.FORTH]: ({ onPreviousClick, onDoneClick }) => (
    <NextPreviousButtons
      onNextClick={onDoneClick}
      onPreviousClick={onPreviousClick}
      nextTitle='Skip'
    />
  )
};
